<script lang="ts" module>
  import { cva, type VariantProps } from "class-variance-authority";

  export const buttonVariants = cva(
    [
      "inline-flex items-center justify-center gap-2",
      "rounded-md font-medium select-none whitespace-nowrap",
      "transition-[background-color,box-shadow,transform,color]",
      "duration-fast ease-standard",
      "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--surface-ring)",
      "disabled:opacity-60 disabled:cursor-not-allowed disabled:pointer-events-none",
      "active:scale-[0.985]",
      "motion-reduce:transition-none motion-reduce:active:scale-100",
    ],
    {
      variants: {
        variant: {
          primary:
            "bg-(--surface-accent) text-(--surface-accent-fg) shadow-soft hover:bg-(--surface-accent-hover)",
          secondary:
            "bg-(--surface-bg-elev) text-(--surface-fg) border border-(--surface-border-strong) hover:bg-(--surface-bg-sunken)",
          ghost: "bg-transparent text-(--surface-fg) hover:bg-(--surface-bg-sunken)",
          danger: "bg-(--surface-danger) text-(--surface-danger-fg) hover:brightness-110",
          link: "bg-transparent p-0 h-auto text-(--surface-accent) hover:underline underline-offset-2",
        },
        size: {
          sm: "h-8 px-3 text-sm",
          md: "h-10 px-4 text-sm",
          lg: "h-12 px-6 text-base",
          icon: "h-10 w-10 p-0",
        },
        block: {
          true: "w-full",
          false: "",
        },
      },
      defaultVariants: {
        variant: "primary",
        size: "md",
        block: false,
      },
      compoundVariants: [
        { variant: "link", size: ["sm", "md", "lg"], class: "h-auto px-0" },
      ],
    },
  );

  export type ButtonVariants = VariantProps<typeof buttonVariants>;
</script>

<script lang="ts">
  import type { HTMLAnchorAttributes, HTMLButtonAttributes } from "svelte/elements";
  import type { Snippet } from "svelte";
  import { cn } from "$lib/utils/cn";

  type Props = ButtonVariants &
    Omit<HTMLButtonAttributes, "class" | "size" | "type"> &
    Omit<HTMLAnchorAttributes, "class" | "size" | "type"> & {
      class?: string;
      children?: Snippet;
      loading?: boolean;
      href?: string;
      type?: HTMLButtonAttributes["type"];
      disabled?: boolean;
    };

  const {
    variant,
    size,
    block,
    class: className,
    children,
    loading = false,
    href,
    type,
    disabled = false,
    ...rest
  }: Props = $props();

  const classes = $derived(cn(buttonVariants({ variant, size, block }), className));
  const resolvedType = $derived<HTMLButtonAttributes["type"]>(
    href ? undefined : (type ?? "button"),
  );
</script>

{#if href}
  <a
    {href}
    class={classes}
    aria-disabled={disabled || loading || undefined}
    tabindex={disabled ? -1 : undefined}
    {...rest as HTMLAnchorAttributes}
  >
    {#if loading}
      <span
        class="size-4 animate-spin rounded-full border-2 border-current border-r-transparent"
        aria-hidden="true"
      ></span>
    {/if}
    {@render children?.()}
  </a>
{:else}
  <button
    type={resolvedType}
    class={classes}
    disabled={disabled || loading}
    {...rest as HTMLButtonAttributes}
  >
    {#if loading}
      <span
        class="size-4 animate-spin rounded-full border-2 border-current border-r-transparent"
        aria-hidden="true"
      ></span>
    {/if}
    {@render children?.()}
  </button>
{/if}
